import isCampaignDataEqual from "./isCampaignDataEqual";

type GetCampaignDataArgs = {
  type?: string;
  campaign?: string;
  channel?: string;
  source?: string;
  segment?: string;
  variant?: string;
  content?: string;
  placement?: string;
  bookingId?: string;
  keyword?: string;
};

const CAMPAIGN_MAPPED_BY_URL: { [key: string]: string } = {
  campaignType: "type",
  campaignChannel: "channel",
  campaignSource: "source",
  campaignName: "campaign",
  campaignSegment: "segment",
  campaignVariant: "variant",
  campaignKeyword: "keyword",
  campaignPlacement: "placement",
  campaignBookingId: "bookingId",
  campaignContent: "content",
};

const CAMPAIGN_CACHED_SESSION_NAME = "campaignCachedData";
const CAMPAIGN_CACHED_SESSION_MAX_LENGTH = 6;

const filterCampaignFields = (data: GetCampaignDataArgs) => {
  if (data.type && data.campaign && data.channel && data.source) {
    const fields = { ...data };
    delete fields.bookingId;
    return { ...fields, ...(data.bookingId && { booking_id: data.bookingId }) };
  }
  return {};
};

const getURLParameters = (paramsString: string) => {
  const result: Record<string, string | undefined> = {};
  const searchParams = new URLSearchParams(paramsString);
  searchParams.forEach((value: string, key: string) => {
    result[key] = value || undefined;
  });
  return result;
};

const getCampaignFromParamsString = (getLocation: () => Location) => {
  const urlParameters = getURLParameters(getLocation().search);
  const campaignFromParamsString = Object.keys(urlParameters).reduce(
    (newData: Record<string, string | undefined>, key: string) => {
      const newKey = CAMPAIGN_MAPPED_BY_URL[key];
      newKey && (newData[newKey] = urlParameters[key]);
      return newData;
    },
    {},
  );
  return filterCampaignFields(campaignFromParamsString);
};

const comparedCampaignData = (
  getLocation: () => Location,
  sessionStorage: () => Storage,
) => {
  try {
    const campaignDataFromUrl = getCampaignFromParamsString(getLocation);

    const campaignDataFromSession = JSON.parse(
      sessionStorage().getItem(CAMPAIGN_CACHED_SESSION_NAME) || "[]",
    );

    const campaignDataExist =
      Array.isArray(campaignDataFromSession) &&
      campaignDataFromSession.some((data) =>
        isCampaignDataEqual(campaignDataFromUrl, data),
      );
    if (!campaignDataExist && JSON.stringify(campaignDataFromUrl) !== "{}") {
      setCampaignSessionStorage(
        campaignDataFromUrl,
        campaignDataFromSession,
        sessionStorage,
      );
      return campaignDataFromUrl;
    } else {
      return {};
    }
  } catch (e) {
    console.error(e);
    return {};
  }
};

const setCampaignSessionStorage = (
  campaignDataFromUrl: Record<string, string>,
  campaignDataFromSession: Record<string, string>[],
  sessionStorage: () => Storage,
) => {
  const campaignDataArray = [campaignDataFromUrl, ...campaignDataFromSession];
  try {
    sessionStorage().setItem(
      CAMPAIGN_CACHED_SESSION_NAME,
      JSON.stringify(
        campaignDataArray.splice(0, CAMPAIGN_CACHED_SESSION_MAX_LENGTH),
      ),
    );
  } catch (e) {
    console.error(e);
  }
};

const getCampaignData =
  (
    getLocation = () => window.location,
    sessionStorage = () => window.sessionStorage,
  ) =>
  // eslint-disable-next-line @typescript-eslint/ban-types
  (): {} => {
    const campaignData = comparedCampaignData(getLocation, sessionStorage);
    return Object.keys(campaignData).length !== 0
      ? {
          campaign: {
            schema: "iglu:au.com.realestate/campaign/jsonschema/1-0-1",
            data: campaignData,
          },
        }
      : campaignData;
  };

export default getCampaignData;
